import { useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties } from 'react'

import { cx } from './cx.ts'

import './form.css'
import './menu.css'
import './combobox.css'

/**
 * 고르거나 적는 칸(combobox) — 입력 상자(`.a-input`) + 앱 드롭다운(`.a-menu`).
 *
 * 🔵 브라우저 `<datalist>` 를 대신한다(eric 2026-09-29 「이거 선택할 때 … 다른 메뉴의 내용이랑 다른데 ..
 *    포맷 좀 맞춰줘」). `datalist` 는 브라우저가 그리는 목록이라 **테두리 · 글자 · 항목 높이 · 다크 테마가
 *    앱과 따로 논다.** 여기서는 정렬 알약(`SortPill`)과 **같은 `.a-menu` 면 · `.a-menu__opt` 항목**을 쓴다 —
 *    새 모양을 짓지 않았다.
 * 🔴 **목록은 제안일 뿐이다.** 목록 밖 값도 그대로 `onChange` 로 나간다 — 막으면 계약에 없는 열거를
 *    화면이 발명한 것이 된다(자유 문자열 칸 · 시료 등록).
 *
 * 키보드(WAI-ARIA combobox · list autocomplete): ↓ 열기 · 다음 · ↑ 이전 · Enter 고르기 · Esc 닫기.
 * 포커스는 늘 입력 상자에 있고, 짚은 항목은 `aria-activedescendant` 가 말한다.
 */
export type ComboboxProps = {
  value: string
  onChange: (value: string) => void
  /** 제안 목록(화면 표기 = 값 · 표기가 다르면 `optionLabel`) */
  options: readonly string[]
  /**
   * 제안의 화면 표기가 값과 다를 때(값은 서버로 나가는 글자 · 표기는 화면 언어 · eric 2026-11-02). 목록 줄과 고른 뒤의 칸이 이 글자를 보이고
   * `onChange` 로는 값이 나간다. 치는 중에는 친 글자 그대로다. 없으면 값이 곧 표기다
   */
  optionLabel?: ((value: string) => string) | undefined
  /** 칸 이름(「종류」) — 펼친 목록의 접근성 이름 */
  label: string
  /** 펼침 단추의 접근성 이름(「종류 목록 열기」) — 원자는 한글을 만들지 않는다 */
  toggleLabel: string
  /** 입력 규칙 위반 — 입력 상자(`.a-input[aria-invalid]`)와 같은 굵고 붉은 테두리. 제출 검증(`useSubmitValidation`)이 준다 */
  invalid?: boolean | undefined
  /** 위반 문구 id(`Field.errorId`) — 칸이 그 문구를 같이 읽는다 */
  describedBy?: string | undefined
  id?: string | undefined
  name?: string | undefined
  'aria-label'?: string | undefined
  /** 고칠 수 없는 «값». 목록도 열리지 않는다 */
  readOnly?: boolean | undefined
  /**
   * 목록 맨 위 「직접 적기」 줄(eric 2026-10-12 「맨 처음에... + 적는 걸로 해줘.. 리스트가 있고.. 처음이나 마지막에.. 없는 거 적는 거」).
   * `empty` = 아무것도 안 쳤을 때의 글(「+ 직접 입력」 — 누르면 칸을 비우고 적게 둔다) · `typed` = 목록에 없는 글을 쳤을 때의 글
   * (「+ 「값」 직접 입력」 — 누르면 그 값으로 닫는다). 원자는 한글을 만들지 않는다. 없으면 줄도 없다
   */
  create?: { empty: string; typed: (text: string) => string } | undefined
  /**
   * 고르기 먼저(eric 2026-10-21 「드랍다운 입풋창은 드랍다운이 되게 하고.. +직접 입력을 클릭해야 입력이 가능하게」) — 칸을 눌러도 글자가
   * 들어가지 않고 목록이 열린다. 「+ 직접 입력」(`create`)을 고른 뒤에만 적을 수 있다. `create` 와 같이 쓴다
   */
  pickFirst?: boolean | undefined
  /** 「직접 입력」으로 목록에 없는 값을 정했을 때 — 부르는 쪽이 그 값을 기억한다(브라우저 저장 등) */
  onCreate?: ((value: string) => void) | undefined
  /**
   * 제안 줄 오른쪽 × — 그 값을 목록에서 뺀다(eric 2026-10-21 「x 버튼 오른쪽에 넣어서.. 이미 저장된거 뺄수도 잇게」).
   * 무엇을 어디서 빼는지는 부르는 쪽이 정한다(이 브라우저의 숨김 등). 칸의 값은 건드리지 않는다. 없으면 × 도 없다.
   * 키보드 — 펼친 목록에서 짚은 줄의 Delete
   */
  onRemove?: ((value: string) => void) | undefined
  /** × 의 접근성 이름(「Patient 목록에서 빼기」) — 원자는 한글을 만들지 않는다. `onRemove` 와 같이 쓴다 */
  removeLabel?: ((value: string) => string) | undefined
}

/** 목록 한 줄 — 맨 위 「직접 적기」(`create`) 또는 제안 */
type Item = { kind: 'create'; text: string; label: string } | { kind: 'option'; text: string }

/**
 * 보일 제안 — 글자를 치는 중이면 그 글자를 품은 것만, 단추 · ↓ 로 열었으면 전부
 * (고른 뒤 다시 열어도 다른 선택지가 다 보이게).
 */
function visibleOptions(
  options: readonly string[],
  query: string | null,
  labelOf: (value: string) => string,
): readonly string[] {
  const q = query?.trim().toLowerCase() ?? ''
  if (q === '') return options
  return options.filter((o) => labelOf(o).toLowerCase().includes(q))
}

export function Combobox({
  value,
  onChange,
  options,
  optionLabel,
  label,
  toggleLabel,
  invalid,
  describedBy,
  id,
  name,
  'aria-label': ariaLabel,
  readOnly,
  create,
  pickFirst = false,
  onCreate,
  onRemove,
  removeLabel,
}: ComboboxProps) {
  const fallbackId = useId()
  const inputId = id ?? fallbackId
  const listId = `${inputId}-list`
  const optionId = (index: number) => `${inputId}-opt-${String(index)}`
  /** 제안 줄의 글 — 줄의 접근성 이름은 이 글만(안의 × 이름이 섞이지 않게) */
  const optionTextId = (index: number) => `${optionId(index)}-text`
  const anchorRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(-1)
  /** 치는 중인 글자 — `null` 이면 걸러내지 않는다 */
  const [query, setQuery] = useState<string | null>(null)
  const locked = readOnly === true
  const labelOf = (text: string) => optionLabel?.(text) ?? text
  /** 고르기 먼저 칸에서 「+ 직접 입력」을 고른 뒤 — 이때만 글자를 친다 */
  const [typing, setTyping] = useState(false)
  const picking = pickFirst && create !== undefined && !typing
  /** 직접 적은 값을 정한다 — 목록에 없는 값이면 부르는 쪽에 알린다 */
  const commitTyped = (text: string) => {
    const v = text.trim()
    if (v !== '' && !options.includes(v)) onCreate?.(v)
    setTyping(false)
  }

  const typed = query?.trim() ?? ''
  const createItem: Item | undefined =
    create === undefined
      ? undefined
      : typed === ''
        ? { kind: 'create', text: '', label: create.empty }
        : options.some((o) => labelOf(o) === typed)
          ? undefined
          : { kind: 'create', text: typed, label: create.typed(typed) }
  const shown: readonly Item[] = [
    ...(createItem === undefined ? [] : [createItem]),
    ...visibleOptions(options, query, labelOf).map((text): Item => ({ kind: 'option', text })),
  ]
  const expanded = open && shown.length > 0
  /** × 가 붙는 줄 — 제안 줄만(「직접 적기」 줄에는 없다) */
  const removable = (item: Item): boolean => onRemove !== undefined && item.kind === 'option'
  /*
   * 펼친 목록은 화면 기준(`position: fixed`)으로 칸 바로 아래에 선다 — 구르는 상자(모달 본문) 안에서 잘리거나 모달에 스크롤을 만들지 않게
   * (eric 2026-10-20 「암종 드랍다운 클릭할 때.. 오른쪽에.. 시료 목록에.. 나오면 안돼」). 아래 자리가 모자라면 칸 위로 편다
   */
  const listRef = useRef<HTMLUListElement | null>(null)
  const [place, setPlace] = useState<CSSProperties | undefined>(undefined)
  useLayoutEffect(() => {
    if (!expanded) return
    const measure = () => {
      const anchor = anchorRef.current?.getBoundingClientRect()
      const list = listRef.current?.getBoundingClientRect()
      if (anchor === undefined) return
      const height = list?.height ?? 0
      const gap = 4
      const below = window.innerHeight - anchor.bottom
      const top =
        below < height + gap && anchor.top > below ? anchor.top - height - gap : anchor.bottom + gap
      setPlace({ left: anchor.left, minWidth: anchor.width, top: Math.max(gap, top) })
    }
    measure()
    window.addEventListener('resize', measure)
    window.addEventListener('scroll', measure, true)
    return () => {
      window.removeEventListener('resize', measure)
      window.removeEventListener('scroll', measure, true)
    }
  }, [expanded, shown.length])

  // 바깥 클릭이면 닫는다 — `SortPill` 과 같은 동작
  useEffect(() => {
    if (!open) return undefined
    const onPointerDown = (e: PointerEvent) => {
      const anchor = anchorRef.current
      if (anchor !== null && e.target instanceof Node && !anchor.contains(e.target)) setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [open])

  /**
   * 단추 · ↓ 로 연다 — 전부 보이고 지금 값을 짚는다. 키보드로 열면 값이 없을 때 첫 항목을 짚는다
   * (마우스로 열었는데 첫 항목이 칠해져 있으면 「이미 골라져 있다」로 읽힌다).
   */
  const openList = (byKey: boolean) => {
    if (locked) return
    // 「직접 적기」 줄이 맨 위에 있으면 제안은 한 칸씩 밀린다
    const offset = create === undefined ? 0 : 1
    const found = options.indexOf(value)
    const current = found < 0 ? -1 : found + offset
    setQuery(null)
    setActive(byKey ? Math.max(0, current) : current)
    setOpen(true)
  }

  /** 제안 하나를 목록에서 뺀다 — 목록은 열린 채, 짚은 자리는 남은 줄 안으로 */
  const remove = (text: string) => {
    if (onRemove === undefined) return
    onRemove(text)
    setActive((i) => Math.min(i, shown.length - 2))
  }

  const choose = (item: Item) => {
    onChange(item.text)
    setOpen(false)
    setActive(-1)
    setQuery(null)
    // 「+ 직접 입력」(빈 글)은 칸을 비우고 그 자리에서 적게 둔다 — 포커스는 늘 입력 상자에 있다(항목의 mousedown 을 막는다)
    if (item.kind === 'create' && item.text === '') {
      if (pickFirst) setTyping(true)
    } else if (item.kind === 'create') commitTyped(item.text)
    else setTyping(false)
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (locked) return
    if (e.key === 'Escape') {
      if (expanded) e.preventDefault()
      setOpen(false)
      return
    }
    // 고르기 먼저 — 닫힌 칸의 스페이스는 목록을 연다(글자는 들어가지 않는다). Enter 는 종전대로 폼으로 흘려 보낸다(저장)
    if (picking && !expanded && e.key === ' ') {
      e.preventDefault()
      openList(true)
      return
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (!expanded) {
        openList(true)
        return
      }
      setActive((i) => (i + 1) % shown.length)
    } else if (e.key === 'ArrowUp') {
      if (!expanded) return
      e.preventDefault()
      setActive((i) => (i <= 0 ? shown.length - 1 : i - 1))
    } else if (e.key === 'Enter') {
      // 펼친 목록의 Enter 는 칸이 받는다 — 짚은 줄을 고르고, 짚은 줄이 없으면 친 글 그대로 닫는다(폼까지 안 간다).
      // 닫힌 칸의 Enter 는 그대로 흘려 보낸다(시료 추가 폼에서는 「저장」)
      if (!expanded) {
        // 직접 적던 칸의 Enter — 적은 값을 정하고 흘려 보낸다(폼의 저장)
        if (typing) commitTyped(value)
        return
      }
      e.preventDefault()
      const item = shown[active]
      if (item !== undefined) choose(item)
      else {
        setOpen(false)
        if (typing) commitTyped(value)
      }
    } else if (e.key === 'Delete' && expanded && onRemove !== undefined) {
      // 짚은 제안 줄만 뺀다 — 짚은 줄이 없거나 「직접 적기」 줄이면 글자 지우기 그대로
      const item = shown[active]
      if (item?.kind !== 'option') return
      e.preventDefault()
      remove(item.text)
    } else if (e.key === 'Tab') {
      setOpen(false)
    }
  }

  return (
    <div className="a-menu-anchor a-combo" ref={anchorRef}>
      <input
        aria-activedescendant={expanded && active >= 0 ? optionId(active) : undefined}
        aria-autocomplete="list"
        aria-controls={listId}
        aria-describedby={describedBy}
        aria-expanded={expanded}
        aria-invalid={invalid === true ? true : undefined}
        aria-label={ariaLabel}
        aria-readonly={locked ? true : undefined}
        autoComplete="off"
        className={cx(
          'a-input',
          'a-input--wide',
          'a-combo__input',
          picking && 'a-combo__input--pick',
          locked && 'a-input--readonly',
        )}
        id={inputId}
        name={name}
        onBlur={typing ? () => commitTyped(value) : undefined}
        onClick={
          picking && !locked
            ? () => {
                if (expanded) setOpen(false)
                else openList(false)
              }
            : undefined
        }
        onChange={
          locked || picking
            ? undefined
            : (e) => {
                onChange(e.target.value)
                setQuery(e.target.value)
                setActive(-1)
                setOpen(true)
              }
        }
        onKeyDown={onKeyDown}
        readOnly={locked || picking}
        ref={inputRef}
        role="combobox"
        type="text"
        value={query === null ? labelOf(value) : value}
      />
      {locked ? null : (
        <button
          aria-controls={listId}
          aria-expanded={expanded}
          aria-label={toggleLabel}
          className="a-combo__toggle"
          onClick={() => {
            if (expanded) setOpen(false)
            else openList(false)
            inputRef.current?.focus()
          }}
          // 🔴 입력 상자의 포커스를 뺏지 않는다 — 뺏으면 키보드 흐름이 단추에 멈춘다
          onMouseDown={(e) => e.preventDefault()}
          tabIndex={-1}
          type="button"
        >
          <span aria-hidden="true" className="a-combo__chevron" />
        </button>
      )}
      {/* 목록 요소는 늘 둔다 — `aria-controls` 가 문서에 없는 id 를 가리키지 않게 */}
      <ul
        aria-label={label}
        className="a-menu a-combo__menu"
        hidden={!expanded}
        id={listId}
        ref={listRef}
        role="listbox"
        style={place}
      >
        {expanded
          ? shown.map((item, index) => (
              <li
                aria-labelledby={removable(item) ? optionTextId(index) : undefined}
                aria-selected={item.kind === 'option' && item.text === value}
                className={cx(
                  'a-menu__opt',
                  item.kind === 'create' && 'a-combo__create',
                  index === active && 'a-menu__opt--active',
                )}
                id={optionId(index)}
                key={`${item.kind}:${item.text}`}
                onClick={() => choose(item)}
                onMouseDown={(e) => e.preventDefault()}
                role="option"
              >
                {item.kind === 'create' ? (
                  item.label
                ) : (
                  <span className="a-combo__opt-text" id={optionTextId(index)}>
                    {labelOf(item.text)}
                  </span>
                )}
                {item.kind === 'option' && item.text === value ? (
                  <span aria-hidden="true" className="a-menu__check">
                    ✓
                  </span>
                ) : null}
                {removable(item) ? (
                  <button
                    aria-label={removeLabel?.(item.text) ?? item.text}
                    className="a-combo__remove"
                    onClick={(e) => {
                      // 🔴 그 줄을 고른 것으로 치지 않는다 · 목록도 닫지 않는다
                      e.stopPropagation()
                      remove(item.text)
                    }}
                    // 🔴 입력 상자의 포커스를 뺏지 않는다(펼침 단추와 같다)
                    onMouseDown={(e) => e.preventDefault()}
                    tabIndex={-1}
                    type="button"
                  >
                    <span aria-hidden="true">×</span>
                  </button>
                ) : null}
              </li>
            ))
          : null}
      </ul>
    </div>
  )
}
